package com.iptvplayer.shared.presentation.viewmodel

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.iptvplayer.shared.data.remote.api.AdminApi
import com.iptvplayer.shared.data.remote.dto.AdminReportsDto
import com.iptvplayer.shared.data.remote.dto.AdminUserDetailDto
import com.iptvplayer.shared.data.remote.dto.AdminUserDto
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch

data class AdminUiState(
    val isLoading: Boolean = false,
    val reports: AdminReportsDto? = null,
    val users: List<AdminUserDto> = emptyList(),
    val detail: AdminUserDetailDto? = null,
    val error: String? = null
)

class AdminViewModel(private val adminApi: AdminApi) : ViewModel() {

    private val _state = MutableStateFlow(AdminUiState())
    val state: StateFlow<AdminUiState> = _state.asStateFlow()

    init { load() }

    fun load() {
        viewModelScope.launch {
            _state.update { it.copy(isLoading = true, error = null) }
            try {
                val reports = adminApi.getReports()
                val users = adminApi.getUsers()
                _state.update { it.copy(isLoading = false, reports = reports, users = users) }
            } catch (e: Exception) {
                _state.update { it.copy(isLoading = false, error = e.message ?: "Failed to load admin data") }
            }
        }
    }

    fun openDetail(id: String) {
        viewModelScope.launch {
            try {
                _state.update { it.copy(detail = adminApi.getUserDetail(id)) }
            } catch (e: Exception) {
                _state.update { it.copy(error = e.message) }
            }
        }
    }

    fun closeDetail() = _state.update { it.copy(detail = null) }

    fun setDisabled(id: String, disabled: Boolean) {
        viewModelScope.launch {
            try { adminApi.setDisabled(id, disabled); load() }
            catch (e: Exception) { _state.update { it.copy(error = e.message) } }
        }
    }

    /** Reject a pending deletion request (clears the flag). */
    fun rejectDeletionRequest(id: String) {
        viewModelScope.launch {
            try { adminApi.setDeletionRequested(id, false); load() }
            catch (e: Exception) { _state.update { it.copy(error = e.message) } }
        }
    }

    /** Approve a deletion request / delete a user: permanent purge. */
    fun deleteUser(id: String) {
        viewModelScope.launch {
            try { adminApi.deleteUser(id); load() }
            catch (e: Exception) { _state.update { it.copy(error = e.message) } }
        }
    }

    fun clearError() = _state.update { it.copy(error = null) }
}
